/**
 * SubscriptionSection
 *
 * Subscription management page for paying users. Shown when the sidebar
 * profile popup's "Manage subscription" entry is clicked. Renders one state
 * out of loading, trial, active, canceling, grace, unpaid, complimentary
 * (Premium granted without a Stripe subscription, e.g. a beta grant), basic
 * (no-history fallback) or error.
 *
 * Premium is one monthly price; the partner rate is a coupon the backend
 * applies to the user's own subscription when they and their linked partner
 * are both on Premium. This page reports it (price line + partner-rate row)
 * but never changes it — payment method, invoices and cancellation live in
 * the Stripe Customer Portal.
 *
 * Cancel flows expand an inline confirmation panel inside the cancel card
 * (no modal), then hand off to the Stripe portal's `subscription_cancel`
 * flow with `?refresh=1` on return so `activate()` kicks off a short
 * polling window while the webhook round-trips.
 */

import { SubscriptionAPI } from '../core/subscriptionApi.js';
import { partnerRateFromStatus, publishSubscriptionStatus } from '../core/subscriptionStatus.js';
import { escapeHtml, eventBus } from '../core/utils.js';

/**
 * Format an ISO date string as "March 5, 2026".
 * Returns a stable empty-string fallback if the input is missing/invalid.
 * @param {string|null|undefined} iso
 * @returns {string}
 * @private
 */
function formatLongDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Short month-and-day date format ("April 30") for warning banner copy
 * where the conversational tone reads better without the year.
 * @param {string|null|undefined} iso
 * @returns {string}
 * @private
 */
function formatShortDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Render a dollar amount without trailing-zero noise: whole dollars stay bare
 * ("$306"), fractional amounts get two decimals ("$25.50").
 * @param {number} amount
 * @returns {string}
 * @private
 */
function formatMoney(amount) {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/**
 * Format the monthly price line used on the active / canceling / trial /
 * grace cards. When the partner rate is applied the discounted amount is
 * quoted and labelled, because that is what the next invoice will say. Falls
 * back to the plan name when the backend hasn't resolved a price (e.g. legacy
 * subscription with an unknown priceId).
 * @param {Object} status - Status payload from SubscriptionAPI.getStatus()
 * @param {string} [fallbackLabel='Premium'] - Label when no price is resolvable
 * @returns {string}
 * @private
 */
function formatPrice(status, fallbackLabel = 'Premium') {
  if (!status || status.amount == null) return fallbackLabel;
  const rate = partnerRateFromStatus(status);
  if (rate.applied && rate.amount != null) {
    return `${formatMoney(rate.amount)}/month · partner rate`;
  }
  return `${formatMoney(status.amount)}/month`;
}

/**
 * @class SubscriptionSection
 */
export class SubscriptionSection {
  /**
   * @param {Object} elements
   * @param {HTMLElement} elements.container - The #subscription-page div
   * @param {EventBus} eventBus
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;

    this.isActive = false;
    this.state = 'loading';
    this.status = null;
    // /config payload (prices, trial). Optional — every consumer falls back to
    // the shipped numbers so a config failure never blanks the page.
    this.config = null;
    this.pollTimer = null;
    // null | 'trial' | 'active' — when set, the cancel card renders
    // its expanded confirmation variant instead of the collapsed link.
    this.cancelConfirm = null;

    // Observed-cancellation analytics state (page-lifetime; see
    // _noteCancelObservation). `_observedCanceling` is null until the first
    // status read; `_cancelConfirmedFired` dedupes the emit — every portal
    // trip is a full-page redirect, so a genuine second cancellation always
    // starts a fresh page lifetime.
    this._observedCanceling = null;
    this._cancelConfirmedFired = false;
    this._refreshActivation = false;

    this.handleContainerClick = this.handleContainerClick.bind(this);
  }

  /**
   * Activate the section — called by the router when /subscription is shown.
   *
   * Always re-fetches status because subscription state is time-sensitive
   * (e.g. user returning from a Stripe portal action must not see stale data).
   *
   * Handles two edge cases the doc (§Section Lifecycle Contract) calls out:
   *   1. Same-page re-entry (teardown-before-reinit): when the router fires
   *      `/subscription` while we're already active, `PageManager.showPage`
   *      early-returns and `deactivateCurrent()` never runs — so we clear
   *      our own timers/listeners up front.
   *   2. Post-await guards: if the user navigates away during `loadStatus()`,
   *      `deactivate()` runs mid-await; we must bail before touching polling
   *      so we don't spin a timer on a section no one is looking at.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.refresh] - True when returning from a state-changing
   *   portal flow; triggers a short polling window while the Stripe webhook
   *   propagates.
   */
  async activate({ refresh = false } = {}) {
    // Teardown-before-reinit — covers same-page re-entry (e.g. user on
    // /subscription re-clicks "Manage subscription" in the profile popup).
    if (this.isActive) {
      this.stopRefreshPolling();
      this.removeListeners();
    }

    this.isActive = true;
    this.state = 'loading';
    this.status = null;
    this.cancelConfirm = null;
    this._refreshActivation = refresh;
    this.render();
    this.attachListeners();

    // Prices first: every card that names a number reads them, so resolving
    // config before status keeps the first real paint correct. A failure here
    // is non-fatal — the renderers fall back to the shipped amounts.
    await this.loadConfig();
    if (!this.isActive) return;

    await this.loadStatus();
    if (!this.isActive) return;

    if (refresh) {
      this.startRefreshPolling();
    }

    console.log('[SubscriptionSection] Activated', { refresh, state: this.state });
  }

  /**
   * Deactivate — called when navigating away.
   */
  deactivate() {
    this.isActive = false;
    this.stopRefreshPolling();
    this.removeListeners();
    console.log('[SubscriptionSection] Deactivated');
  }

  /**
   * Fetch subscription config (prices, trial). Best-effort: on any failure
   * `this.config` stays null and the renderers use their shipped fallbacks.
   * @private
   */
  async loadConfig() {
    try {
      const result = await SubscriptionAPI.getConfig();
      if (result?.success && result.data) {
        this.config = result.data;
      }
    } catch (err) {
      console.warn('[SubscriptionSection] loadConfig failed:', err?.message || err);
    }
  }

  /**
   * Fetch the latest subscription status and re-render.
   *
   * If the derived state is 'basic' (no subscription history), redirect
   * straight to /upgrade without rendering — avoids a visible Basic-card
   * flash for users who shouldn't have been routed here. Dev previews bypass
   * this by setting `status` directly and skipping loadStatus().
   *
   * @private
   */
  async loadStatus() {
    let result;
    try {
      result = await SubscriptionAPI.getStatus();
    } catch (err) {
      console.error('[SubscriptionSection] loadStatus failed:', err);
      if (!this.isActive) return;
      this.state = 'error';
      this.status = null;
      this.render();
      this.attachListeners();
      return;
    }

    // Post-await guard: user may have navigated away during the fetch.
    if (!this.isActive) return;

    if (!result?.success) {
      // 401 → session expired; route to login. Other failures fall through
      // to the generic error card.
      if (result?.status === 401) {
        window.location.href = '/login?returnTo=/subscription';
        return;
      }
      this.state = 'error';
      this.status = null;
    } else {
      this.adoptStatus(result.data);
      // Fresh status from server — collapse any open inline confirm.
      this.cancelConfirm = null;
    }

    // Basic users (no subscription history) shouldn't be on /subscription —
    // bounce them immediately without an intermediate render. Dev previews
    // never hit this branch because they set status + state directly.
    if (this.state === 'basic') {
      eventBus.emit('navigate', { page: 'upgrade' });
      return;
    }

    this.render();
    this.attachListeners();
  }

  /**
   * Adopt a fresh status payload: local state, the shared snapshot, and the
   * derived UI state, in that order.
   *
   * Publishing to the snapshot is what keeps the passive plan-aware surfaces
   * (sidebar badge, header upgrade button, analytics) in step with a plan
   * change made on this page — they re-render off `subscription:status` and
   * never refetch on their own. This page fetches through `SubscriptionAPI`
   * rather than `fetchSubscriptionStatus` because its failure envelope is
   * load-bearing (401 → login, anything else → error card).
   *
   * @param {Object} status - Status payload from /status
   * @private
   */
  adoptStatus(status) {
    this.status = status;
    publishSubscriptionStatus(status);
    this.state = this.deriveStateFromStatus(status);
    this._noteCancelObservation(this.state === 'canceling');
  }

  /**
   * Emit `subscription:cancel-confirmed` when a status read derives the
   * canceling state for a subscription that wasn't previously canceling.
   *
   * This records OBSERVED cancellation (the webhook landed and the status
   * reflects it), not intent — intent is `subscription:cancel-portal-started`.
   * Eligible transitions: a prior read in this page lifetime was
   * non-canceling (the `?refresh=1` polling window catching the flip), or
   * the first read of a `?refresh=1` activation already shows canceling (the
   * webhook beat the portal return). A plain page load that finds an
   * already-canceling subscription does NOT emit — that cancellation was
   * counted when it happened.
   *
   * @param {boolean} isCanceling - Whether this read derived 'canceling'
   * @private
   */
  _noteCancelObservation(isCanceling) {
    const prior = this._observedCanceling;
    this._observedCanceling = isCanceling;
    if (!isCanceling || this._cancelConfirmedFired) return;

    const eligible = prior === false || (prior === null && this._refreshActivation);
    if (!eligible) return;

    this._cancelConfirmedFired = true;
    this.eventBus.emit('subscription:cancel-confirmed', {});
  }

  /**
   * Map backend status payload → UI state token.
   *
   * UNPAID / CANCELED lapse users stay on /subscription and see an in-place
   * reactivation card (intentional — they have Premium history and may want
   * to resume without losing context). First-time FREE users go through the
   * 'basic' redirect branch.
   *
   * The partner rate never changes the state — it's a discount on the same
   * subscription, rendered inside the active card.
   *
   * @param {Object|null} status
   * @returns {'trial'|'active'|'canceling'|'grace'|'unpaid'|'complimentary'|'basic'}
   */
  deriveStateFromStatus(status) {
    if (!status || !status.isSubscribed) {
      if (status?.status === 'UNPAID' || status?.status === 'CANCELED') return 'unpaid';
      return 'basic';
    }
    // Complimentary access (e.g. beta grant): Premium without a Stripe
    // subscription of the user's own — nothing to bill, cancel or manage.
    // Strict false check: legacy payloads lack the field and stay 'active'.
    if (status.hasOwnPaidSubscription === false) return 'complimentary';
    if (status.status === 'PAST_DUE') return 'grace';
    // Before the TRIALING check so a canceled trial shows the canceling card
    // (resume affordance) instead of a live trial card promising a charge.
    if (status.cancelAtPeriodEnd) return 'canceling';
    if (status.status === 'TRIALING') return 'trial';
    return 'active';
  }

  /**
   * Whether the backend offers the partner rate (coupon configured). Config
   * presence is the launch flag — without it the page never mentions the rate.
   * @returns {boolean}
   * @private
   */
  isPartnerRateAvailable() {
    return this.config?.partnerRate?.available === true;
  }

  /**
   * Discounted per-person monthly amount, preferring the status payload (what
   * Stripe actually bills) over config, with the shipped number as a last
   * resort so copy never renders a blank.
   * @returns {number}
   * @private
   */
  partnerRateAmount() {
    const fromStatus = partnerRateFromStatus(this.status).amount;
    return fromStatus ?? this.config?.partnerRate?.amount ?? 15;
  }

  /**
   * Premium list price per month, from config where available.
   * @returns {number}
   * @private
   */
  monthlyListAmount() {
    return this.config?.prices?.monthly?.amount ?? 20;
  }

  /**
   * Render the current state into the container.
   * @private
   */
  render() {
    const body = this.renderBody();
    const faq = this.renderFAQ();
    this.container.innerHTML = `
      <div class="subscription-page">
        <header class="subscription-header">
          <h1 class="subscription-header__title">Subscription</h1>
        </header>
        ${body}
        ${faq}
      </div>
    `;
  }

  /**
   * Render the state-specific body HTML.
   * @returns {string}
   * @private
   */
  renderBody() {
    switch (this.state) {
      case 'loading':
        return this.renderLoading();
      case 'error':
        return this.renderError();
      case 'trial':
        return this.renderTrial();
      case 'active':
        return this.renderActive();
      case 'canceling':
        return this.renderCanceling();
      case 'grace':
        return this.renderGrace();
      case 'unpaid':
        return this.renderUnpaid();
      case 'complimentary':
        return this.renderComplimentary();
      // 'basic' never renders — loadStatus() redirects before render.
      default:
        return this.renderError();
    }
  }

  renderLoading() {
    return `
      <section class="subscription-plan subscription-plan--skeleton" aria-busy="true">
        <div class="subscription-skeleton-line subscription-skeleton-line--title"></div>
        <div class="subscription-skeleton-line"></div>
        <div class="subscription-skeleton-line subscription-skeleton-line--short"></div>
      </section>
    `;
  }

  renderError() {
    return `
      <section class="subscription-plan subscription-plan--error">
        <p class="subscription-plan__body">Couldn't load subscription details.</p>
        <div class="subscription-plan__actions">
          <button type="button" data-action="retry" class="btn btn--primary">Try again</button>
        </div>
      </section>
    `;
  }

  renderTrial() {
    const trialEnd = formatLongDate(this.status?.currentPeriodEnd);
    // A partner rate attached at Checkout already discounts the first charge.
    const rate = partnerRateFromStatus(this.status);
    const firstAmount = rate.applied && rate.amount != null ? rate.amount : this.status?.amount;
    const price = firstAmount != null ? formatMoney(firstAmount) : '';
    const firstChargeLine = trialEnd && price
      ? `First payment: ${escapeHtml(price)} on ${escapeHtml(trialEnd)}.`
      : '';
    return `
      <section class="subscription-plan subscription-plan--trial">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Premium</h2>
          <span class="subscription-plan__tag">Trial</span>
        </div>
        <p class="subscription-plan__body">
          Your free trial ends ${escapeHtml(trialEnd)}.
          ${firstChargeLine}
        </p>
        <div class="subscription-plan__actions">
          <button type="button" data-action="manage-billing" class="btn btn--primary">Manage billing in Stripe</button>
        </div>
        <p class="subscription-plan__hint">Update payment method or view upcoming invoices.</p>
      </section>
      ${this.renderCancelCard('trial')}
    `;
  }

  renderActive() {
    const renews = formatLongDate(this.status?.currentPeriodEnd);
    const priceLine = `${escapeHtml(formatPrice(this.status))} · Renews ${escapeHtml(renews)}`;
    return `
      <section class="subscription-plan">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Premium</h2>
          <span class="subscription-plan__tag subscription-plan__tag--premium">Active</span>
        </div>
        <p class="subscription-plan__body">${priceLine}</p>
        ${this.renderPartnerRateRow()}
        <div class="subscription-plan__actions">
          <button type="button" data-action="manage-billing" class="btn btn--primary">Manage billing in Stripe</button>
        </div>
        <p class="subscription-plan__hint">Update payment method or view invoices.</p>
      </section>
      ${this.renderCancelCard('active')}
    `;
  }

  /**
   * Render the partner-rate row inside the active card. Three variants:
   *   - applied → the discounted next bill and its date
   *   - linked but not applied → the rate starts once the partner is on Premium
   *   - not linked → the offer, pointing at /partner
   * Renders nothing when the backend hasn't configured the rate.
   *
   * Linked state is read off `window.app.currentUser.coupleId` (set on
   * bootstrap, cleared on `partner:unlinked`) — no extra fetch.
   *
   * @returns {string}
   * @private
   */
  renderPartnerRateRow() {
    if (!this.isPartnerRateAvailable()) return '';

    const rate = partnerRateFromStatus(this.status);
    const amount = escapeHtml(formatMoney(this.partnerRateAmount()));
    const isLinked = Boolean(window.app?.currentUser?.coupleId);

    let modifier;
    let body;
    if (rate.applied) {
      const nextBill = formatLongDate(this.status?.currentPeriodEnd);
      modifier = 'applied';
      body = nextBill
        ? `Partner rate active — your next bill is ${amount} on ${escapeHtml(nextBill)}.`
        : `Partner rate active — your next bill is ${amount}.`;
    } else if (isLinked) {
      modifier = 'pending';
      body = 'Partner rate: starts when your partner is on Premium too.';
    } else {
      modifier = 'offer';
      body = `<button type="button" data-action="go-partner" class="subscription-partner-rate__link">Link with your partner</button> for ${amount}/month each.`;
    }

    return `
        <div class="subscription-partner-rate subscription-partner-rate--${modifier}">
          <p class="subscription-partner-rate__body">${body}</p>
        </div>`;
  }

  renderCanceling() {
    const endDate = formatLongDate(this.status?.currentPeriodEnd);
    const shortEndDate = formatShortDate(this.status?.currentPeriodEnd);
    const priceLine = `${escapeHtml(formatPrice(this.status))} · Access ends ${escapeHtml(endDate)}`;
    const warning = `After ${escapeHtml(shortEndDate)}, you'll return to Basic. Your insights and memory will still be there, but you'll have limited access until you resubscribe.`;

    return `
      <section class="subscription-plan subscription-plan--canceling">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Premium</h2>
          <span class="subscription-plan__tag subscription-plan__tag--warning">Canceling</span>
        </div>
        <p class="subscription-plan__body">${priceLine}</p>
        <div class="subscription-warning">
          <p>${warning}</p>
        </div>
        <div class="subscription-plan__actions">
          <button type="button" data-action="resume" class="btn btn--primary">Resume subscription</button>
          <button type="button" data-action="manage-billing" class="btn btn--secondary">Manage billing in Stripe</button>
        </div>
      </section>
    `;
  }

  renderGrace() {
    const priceLine = `${escapeHtml(formatPrice(this.status))} · Renewal pending`;
    const shortEndDate = formatShortDate(this.status?.gracePeriodEndsAt || this.status?.currentPeriodEnd);
    return `
      <section class="subscription-plan subscription-plan--grace">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Premium</h2>
          <span class="subscription-plan__tag subscription-plan__tag--danger">Payment issue</span>
        </div>
        <p class="subscription-plan__body">${priceLine}</p>
        <div class="subscription-warning subscription-warning--danger">
          <p>We couldn't charge your card. Your Premium access ends ${escapeHtml(shortEndDate)} unless you update your payment method.</p>
        </div>
        <div class="subscription-plan__actions">
          <button type="button" data-action="update-payment" class="btn btn--danger">Update payment method</button>
          <button type="button" data-action="manage-billing" class="btn btn--secondary">Manage billing in Stripe</button>
        </div>
      </section>
      ${this.renderCancelCard('active')}
    `;
  }

  renderUnpaid() {
    const endDate = formatLongDate(this.status?.currentPeriodEnd);
    const endedLine = endDate
      ? `Your Premium access ended ${escapeHtml(endDate)}.`
      : 'Your Premium access has ended.';
    // Reactivation quotes the monthly Premium price, from config where available.
    const monthly = formatMoney(this.monthlyListAmount());
    return `
      <section class="subscription-plan subscription-plan--ended">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Subscription ended</h2>
        </div>
        <p class="subscription-plan__body">${endedLine}</p>
        ${this.renderLockedDataStats()}
        <div class="subscription-plan__actions">
          <button type="button" data-action="reactivate" class="btn btn--primary">Reactivate — ${monthly}/month</button>
        </div>
        <p class="subscription-plan__hint">Billed monthly. Cancel anytime.</p>
      </section>
    `;
  }

  /**
   * Render the complimentary-access card. These users are Premium without a
   * Stripe subscription of their own (all billing fields are null), so none
   * of the billing surfaces apply — no portal buttons, no cancel card, no
   * partner-rate row, no price line.
   * @returns {string}
   * @private
   */
  renderComplimentary() {
    return `
      <section class="subscription-plan">
        <div class="subscription-plan__header">
          <h2 class="subscription-plan__name">Premium</h2>
          <span class="subscription-plan__tag subscription-plan__tag--premium">Complimentary</span>
        </div>
        <p class="subscription-plan__body">Your beta access includes Premium at no charge — there's nothing to manage or pay.</p>
      </section>
    `;
  }

  /**
   * Render the value-accrual "Everything you've built is still here" block
   * inside the ended-subscription card. Pulls `retentionStats` from the
   * backend status payload; falls back to nothing when the stats object is
   * missing (old backend or fresh-lapsed user with no computed data yet).
   *
   * The framing is accrual, not loss: what the user built stays on Basic, and
   * Premium brings back unlimited conversations. Shows all three stats even
   * when counts are small — honesty over manipulation. Singular/plural wording
   * switches on count.
   *
   * @returns {string}
   * @private
   */
  renderLockedDataStats() {
    const stats = this.status?.retentionStats;
    if (!stats) return '';

    const { insightCount = 0, memoryMonths = 0, discoveryTopicCount = 0 } = stats;

    const items = [
      { count: insightCount, suffix: insightCount === 1 ? 'insight' : 'insights' },
      { count: memoryMonths, suffix: memoryMonths === 1 ? 'month of memory' : 'months of memory' },
      { count: discoveryTopicCount, suffix: discoveryTopicCount === 1 ? 'discovery topic' : 'discovery topics' }
    ];

    const lockIcon = `<svg class="subscription-locked__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>`;

    const itemsHtml = items.map(item => `
      <div class="subscription-locked__item">
        ${lockIcon}
        <span><strong>${item.count}</strong> ${escapeHtml(item.suffix)}</span>
      </div>
    `).join('');

    return `
      <div class="subscription-locked">
        <h3 class="subscription-locked__title">Everything you've built is still here</h3>
        <div class="subscription-locked__items">
          ${itemsHtml}
        </div>
        <p class="subscription-locked__footer">Your insights, memory and discovery progress stay on Basic. Premium brings back unlimited conversations.</p>
      </div>
    `;
  }

  /**
   * Render the cancel card that sits below the plan container. Two variants:
   *   - Collapsed: quiet "Need to cancel?" row + red text link
   *   - Expanded: warning copy + Yes/Never-mind buttons
   *
   * Expansion is toggled by this.cancelConfirm (set from handleAction).
   *
   * @param {'trial'|'active'} variant
   * @returns {string}
   * @private
   */
  renderCancelCard(variant) {
    const isTrial = variant === 'trial';
    const linkLabel = isTrial ? 'Cancel trial' : 'Cancel subscription';

    if (this.cancelConfirm !== variant) {
      return `
        <section class="subscription-cancel">
          <span class="subscription-cancel__label">Need to cancel?</span>
          <button type="button" data-action="cancel-${variant}" class="subscription-cancel__link">${linkLabel}</button>
        </section>
      `;
    }

    const endDate = formatLongDate(this.status?.currentPeriodEnd);
    // The partner's rate depends on this subscription staying Premium. Neutral
    // wording: a beta-grant partner isn't billed, so a price claim could be
    // false.
    const partnerClause = partnerRateFromStatus(this.status).applied
      ? ' If your partner has the partner rate, it ends at their next bill.'
      : '';
    const warning = isTrial
      ? `Canceling stops your upcoming charge — you won't be billed, and you keep full access until your trial ends on ${escapeHtml(endDate)}. After that you'll be on Basic: your insights, memory, and discovery progress stay right where they are, and the daily message limit returns. Upgrading again any time removes the limit.`
      : `This is a significant step. When your billing period ends on ${escapeHtml(endDate)}, you'll drop to Basic and lose full access to the therapeutic insights, clinical memory, and personalized guidance you've built during your Premium time.${partnerClause} Your daily message limit returns, and your hard-earned progress will only be visible in a limited view. Your data stays safe — re-upgrading any time restores everything — but your therapeutic continuity will be interrupted.`;
    const confirmLabel = isTrial ? 'Yes, cancel trial' : 'Yes, cancel';

    return `
      <section class="subscription-cancel subscription-cancel--expanded">
        <h3 class="subscription-cancel__title">Are you sure you want to cancel?</h3>
        <p class="subscription-cancel__body">${warning}</p>
        <div class="subscription-cancel__buttons">
          <button type="button" data-action="confirm-cancel" class="btn btn--danger">${confirmLabel}</button>
          <button type="button" data-action="dismiss-cancel" class="btn btn--secondary">Never mind</button>
        </div>
      </section>
    `;
  }

  /**
   * Render a state-aware "Common questions" FAQ block. Questions differ
   * by state so users get answers relevant to *their* subscription —
   * e.g., an active subscriber sees "When will I be charged next?" while
   * a canceling user sees "Can I change my mind?" instead.
   *
   * Returns an empty string for loading/error/basic states where an FAQ
   * would be noise (no subscription context to answer against).
   *
   * Markup mirrors the shared .faq component (see css/components/faq.css);
   * toggling is delegated through handleAction's 'faq-toggle' case.
   *
   * @returns {string}
   * @private
   */
  renderFAQ() {
    const items = this.getFAQItems();
    if (!items.length) return '';

    const rows = items.map(({ q, a }) => `
      <div class="faq__item">
        <button type="button" class="faq__question" data-action="faq-toggle">
          <span>${q}</span>
          <span class="faq__icon">+</span>
        </button>
        <div class="faq__answer">
          <p>${a}</p>
        </div>
      </div>
    `).join('');

    return `
      <div class="faq">
        <h3 class="faq__title">Common questions</h3>
        <div class="faq__list">
          ${rows}
        </div>
      </div>
    `;
  }

  /**
   * Return the [{ q, a }] list for the current state. All dynamic copy
   * (dates, prices) is escaped via escapeHtml before interpolation.
   *
   * @returns {Array<{q: string, a: string}>}
   * @private
   */
  getFAQItems() {
    const endDate = escapeHtml(formatLongDate(this.status?.currentPeriodEnd));
    const graceEnd = escapeHtml(
      formatLongDate(this.status?.gracePeriodEndsAt || this.status?.currentPeriodEnd)
    );
    const monthlyAmount = escapeHtml(formatMoney(this.monthlyListAmount()));
    const partnerAmount = escapeHtml(formatMoney(this.partnerRateAmount()));

    switch (this.state) {
      case 'trial':
        return [
          {
            q: 'What happens if I cancel during the trial?',
            a: "You won't be charged — canceling stops your upcoming payment. You keep full access until your trial ends, then move to Basic. Everything you've built — insights, memory, discovery progress — stays in your account; the daily message limit returns."
          },
          {
            q: 'Will I lose my therapeutic progress if I cancel?',
            a: "No — everything stays in your account. On Basic you'll see a limited view, and re-upgrading any time restores full access to insights, memory, and guidance."
          },
          {
            q: 'Can I update my payment method before my first charge?',
            a: 'Yes. Use "Manage billing in Stripe" above to update your card any time before your trial ends.'
          }
        ];

      case 'active':
        return [
          ...(this.isPartnerRateAvailable() ? [
            {
              q: "What's the partner rate?",
              a: `Each of you subscribes to Premium. When you and your linked partner are both on Premium, you each pay ${partnerAmount}/month automatically instead of ${monthlyAmount}. It ends at your next bill if you unlink or your partner's Premium ends.`
            },
            {
              q: 'What happens to my price if we unlink?',
              a: `Your Premium continues at ${monthlyAmount}/month from your next bill.`
            }
          ] : []),
          {
            q: 'What happens to my insights if I cancel later?',
            a: "You'll keep Premium until your period ends, then drop to Basic — which shows only a limited view of the insights and memory built during Premium. All your data stays safe, and re-upgrading any time fully restores access."
          },
          {
            q: 'How do I update my payment method?',
            a: 'Click "Manage billing in Stripe" above, then choose "Update payment method". Changes apply to your next charge.'
          },
          {
            q: 'Can I get a refund on a recent charge?',
            a: 'Refund requests are handled through Stripe. Open "Manage billing in Stripe" to review past invoices and contact support with your charge details.'
          }
        ];

      case 'canceling':
        return [
          {
            q: 'Can I change my mind?',
            a: `Yes. Click "Resume subscription" any time before ${endDate} and your access continues uninterrupted — no new charge, no loss of access.`
          },
          {
            q: 'Will I be charged again?',
            a: `No. Your current period runs through ${endDate}, and no further charges will occur unless you resume.`
          },
          {
            q: `What happens to my insights after ${endDate}?`,
            a: "They stay in your account. On Basic you'll see a limited view of the insights and memory built during Premium. Re-upgrading any time fully restores access."
          },
          {
            q: 'Will any of my data be deleted?',
            a: "No. Your therapeutic data is preserved regardless of subscription status — only the level of access to it changes."
          }
        ];

      case 'grace':
        return [
          {
            q: 'Why is there a payment issue?',
            a: "Your most recent charge failed — usually an expired card, insufficient funds, or a bank block. Updating your payment method above is typically all it takes to fix it."
          },
          {
            q: 'Will Stripe try my card again?',
            a: `Yes. Stripe automatically retries failed payments for a few days. If all retries fail, your Premium access ends ${graceEnd}.`
          },
          {
            q: 'What happens to my data if my access ends?',
            a: "It stays safe. You'll see a limited view of your insights and memory on Basic until the payment issue is resolved. No therapeutic data is deleted."
          },
          {
            q: 'Can I resolve this without losing access?',
            a: `Yes. Update your payment method before ${graceEnd} and Stripe will re-attempt billing. Your Premium access continues uninterrupted if the next charge succeeds.`
          }
        ];

      case 'unpaid':
        return [
          {
            q: 'Can I get my full access back?',
            a: 'Yes. Click "Reactivate subscription" above and your past insights, memory, and personalized guidance are fully restored the moment payment succeeds.'
          },
          {
            q: 'Is my data still there?',
            a: "Yes — all your therapeutic data is preserved. You're currently seeing a limited view on Basic, and reactivating restores the full picture."
          },
          {
            q: 'Will I be charged for the time my access was ended?',
            a: "No. Billing restarts fresh when you reactivate — you're only charged going forward, never retroactively."
          },
          {
            q: 'Why did my Premium access end?',
            a: 'Either billing failed repeatedly or you previously canceled. Either way, reactivating is a clean fresh start — no penalties, no back-charges.'
          }
        ];

      // Complimentary access has no billing to explain — every question here
      // would point at Stripe controls the user doesn't have.
      case 'complimentary':
        return [];

      // loading / error / basic — no FAQ; the user has no state context yet.
      default:
        return [];
    }
  }

  /**
   * Single delegated click listener on the container — captures all
   * data-action button clicks. Survives re-renders because we re-attach
   * after every render() call.
   * @private
   */
  attachListeners() {
    this.container.addEventListener('click', this.handleContainerClick);
  }

  /** @private */
  removeListeners() {
    this.container.removeEventListener('click', this.handleContainerClick);
  }

  /** @private */
  handleContainerClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    if (!action) return;
    event.preventDefault();
    this.handleAction(action, target);
  }

  /**
   * Dispatch data-action clicks to the appropriate flow. The triggering
   * button is passed through so async portal actions can disable it to
   * prevent double-submits (re-enabled by goToPortal on failure).
   *
   * @param {string} action
   * @param {HTMLElement|null} [triggerEl] - Button that initiated the action
   * @private
   */
  async handleAction(action, triggerEl = null) {
    const origin = window.location.origin;

    // Disable the trigger for actions that either navigate or open a modal,
    // to guard against rapid double-clicks.
    const disableTrigger = () => {
      if (triggerEl && 'disabled' in triggerEl) triggerEl.disabled = true;
    };
    const enableTrigger = () => {
      if (triggerEl && 'disabled' in triggerEl) triggerEl.disabled = false;
    };

    switch (action) {
      case 'retry':
        this.state = 'loading';
        this.render();
        this.attachListeners();
        await this.loadStatus();
        break;

      case 'manage-billing':
        // Plain portal — no flow_data, no polling on return.
        disableTrigger();
        await this.goToPortal({ returnUrl: `${origin}/subscription` }, triggerEl);
        break;

      case 'update-payment':
        disableTrigger();
        await this.goToPortal({
          flowType: 'payment_method_update',
          returnUrl: `${origin}/subscription?refresh=1`
        }, triggerEl);
        break;

      case 'resume':
        // Stripe Portal has no deep-link for un-cancel; home screen carries it.
        disableTrigger();
        await this.goToPortal({ returnUrl: `${origin}/subscription?refresh=1` }, triggerEl);
        break;

      case 'cancel-trial':
      case 'cancel-active':
        // Expand the cancel card inline. No trigger-disable — re-render
        // replaces the button entirely.
        this.cancelConfirm = action === 'cancel-trial' ? 'trial' : 'active';
        this.render();
        this.attachListeners();
        break;

      case 'dismiss-cancel':
        this.cancelConfirm = null;
        this.render();
        this.attachListeners();
        break;

      case 'confirm-cancel':
        // Hand off to Stripe Portal's subscription_cancel flow. Same portal
        // handoff for both trial and active cancellations (Stripe distinguishes
        // by subscription status). The emit records cancellation INTENT (the
        // portal opened); observed cancellation is a later status read
        // deriving 'canceling' (see _noteCancelObservation).
        disableTrigger();
        eventBus.emit('subscription:cancel-portal-started', {});
        await this.goToPortal({
          flowType: 'subscription_cancel',
          returnUrl: `${origin}/subscription?refresh=1`
        }, triggerEl);
        break;

      case 'reactivate':
        disableTrigger();
        eventBus.emit('subscription:reactivate-clicked', {});
        eventBus.emit('navigate', { page: 'upgrade' });
        break;

      case 'go-partner':
        disableTrigger();
        eventBus.emit('navigate', { page: 'partner' });
        break;

      case 'faq-toggle': {
        // In-place accordion toggle. No re-render — mutate classes directly
        // so the CSS transition (grid-template-rows) animates smoothly.
        const item = triggerEl?.closest('.faq__item');
        if (!item) break;
        const isOpen = item.classList.toggle('faq__item--open');
        const icon = triggerEl.querySelector('.faq__icon');
        if (icon) icon.textContent = isOpen ? '−' : '+';
        break;
      }

      default:
        enableTrigger();
        console.warn('[SubscriptionSection] Unknown action:', action);
    }
  }

  /**
   * Redirect to the Stripe Customer Portal. Surfaces failures via the
   * global NotificationManager instead of leaving the user on a dead click.
   * Re-enables the trigger button on failure so the user can retry.
   *
   * @param {Object} options - Forwarded to SubscriptionAPI.redirectToPortal()
   * @param {HTMLElement} [triggerEl] - Button that initiated the action
   * @private
   */
  async goToPortal(options, triggerEl = null) {
    try {
      await SubscriptionAPI.redirectToPortal(options);
    } catch (err) {
      console.error('[SubscriptionSection] Portal redirect failed:', err);
      if (triggerEl && 'disabled' in triggerEl) {
        triggerEl.disabled = false;
      }
      window.notificationManager?.showError(
        'Unable to open billing portal. Please try again.'
      );
    }
  }

  /**
   * Poll `/status` every 2s (up to 5 attempts) looking for a change versus
   * the snapshot captured at activate() time. Stops as soon as any relevant
   * field flips, or after the attempt budget runs out.
   *
   * Used when returning from a state-changing portal flow via `?refresh=1`.
   * @private
   */
  startRefreshPolling() {
    this.stopRefreshPolling();

    const baseline = this._snapshotKey(this.status);
    const maxAttempts = 5;
    const intervalMs = 2000;
    let attempts = 0;

    const scheduleNext = () => {
      if (attempts >= maxAttempts || !this.isActive) return;
      this.pollTimer = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      if (!this.isActive) return;
      attempts += 1;
      try {
        const result = await SubscriptionAPI.getStatus();
        if (!this.isActive) return;
        if (result?.success) {
          const nextKey = this._snapshotKey(result.data);
          if (nextKey !== baseline) {
            this.adoptStatus(result.data);
            this.render();
            this.attachListeners();
            this.stopRefreshPolling();
            this._stripRefreshParam();
            return;
          }
        }
      } catch (err) {
        console.warn('[SubscriptionSection] Poll attempt failed:', err?.message || err);
      }
      scheduleNext();
    };

    this.pollTimer = setTimeout(tick, intervalMs);
  }

  /** @private */
  stopRefreshPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /**
   * Key used to detect a meaningful change during polling. The post-Portal
   * `?refresh=1` return is waiting for a cancellation, resume or payment fix —
   * all of which move one of these three fields.
   *
   * @param {Object|null} status
   * @returns {string}
   * @private
   */
  _snapshotKey(status) {
    if (!status) return 'null';
    return JSON.stringify({
      status: status.status,
      cancelAtPeriodEnd: status.cancelAtPeriodEnd,
      isSubscribed: status.isSubscribed
    });
  }

  /**
   * Remove ?refresh=1 from the URL bar so a page refresh doesn't re-enter
   * the polling branch. Uses replaceState to avoid a history entry.
   * @private
   */
  _stripRefreshParam() {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('refresh')) {
        url.searchParams.delete('refresh');
        const newUrl = url.pathname + (url.search ? url.search : '') + url.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch {
      // URL API failure is non-critical — leave the param in place.
    }
  }
}

export default SubscriptionSection;
